import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, of } from 'rxjs';
import { SwitchWorkspaceModal } from '@/app/views/modals/switch-workspace-modal/switch-workspace-modal';
import { AuthService } from '@core/services/auth.service';
import { UserService } from '@core/services/user.service';
import { UserWorkspaceContextService } from '@core/services/user-workspace-context.service';

const DEFAULT_PROFILE_AVATAR_URL =
  '/assets/img/demo/avatars/avatar-default-cat-50.webp';

@Component({
  selector: 'app-profile-dropdown',
  imports: [NgbDropdownModule, RouterLink],
  templateUrl: './profile-dropdown.html',
  styles: `
    :host {
      position: relative;
      z-index: calc(var(--z-index-master, 999) + 3);
    }

    .dropdown-menu {
      z-index: calc(var(--z-index-master, 999) + 4) !important;
    }

    .profile-dropdown-menu {
      min-width: 218px;
    }

    .profile-context-text {
      min-width: 0;
    }

    .profile-context-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    :host .btn-system .profile-dropdown-toggle-avatar {
      width: 3.125rem;
      height: 3.125rem;
      max-width: 3.125rem;
      max-height: 3.125rem;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileDropdown implements OnInit {
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly workspaceContextService = inject(
    UserWorkspaceContextService,
  );
  private readonly document = inject(DOCUMENT);
  private readonly currentContext = signal<{
    accountName: string;
    workspaceName: string;
  } | null>(null);

  protected readonly profileImageUrl = computed(
    () =>
      this.userService.currentUser()?.avatarReadUrls?.size50 ||
      DEFAULT_PROFILE_AVATAR_URL,
  );

  protected readonly profileEmail = computed(
    () => this.userService.currentUser()?.email?.trim() || 'Open Profile Dropdown',
  );

  protected get firstName(): string {
    const userName = this.authService.getUserName()?.trim();

    return userName?.split(/\s+/)[0] || 'User';
  }

  protected get profileImageAlt(): string {
    return `${this.firstName} profile picture`;
  }

  protected readonly accountName = computed(
    () => this.currentContext()?.accountName ?? null,
  );
  protected readonly workspaceName = computed(
    () => this.currentContext()?.workspaceName ?? null,
  );

  protected get canAccessTeam(): boolean {
    return this.authService.hasAnyValidPermission([
      'UserManager',
      'Workspace',
      'Role',
    ]);
  }

  ngOnInit(): void {
    this.loadCurrentContext();

    const userId = this.authService.GetUser();
    if (typeof userId !== 'number' || userId <= 0) {
      return;
    }

    this.userService
      .getById(userId)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  protected truncateContextName(name: string): string {
    return name.length > 15 ? `${name.slice(0, 15)}…` : name;
  }

  print() {
    window.print();
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  openSwitchWorkspace(): void {
    const modalRef = this.modalService.open(SwitchWorkspaceModal, {
      size: 'md',
      backdrop: 'static',
    });

    modalRef.closed.subscribe((result: { switched?: boolean } | undefined) => {
      if (!result?.switched) {
        return;
      }

      void this.router.navigate(['/home']).finally(() => {
        this.reloadApplication();
      });
    });
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }

  private reloadApplication(): void {
    this.document.defaultView?.location.reload();
  }

  private loadCurrentContext(): void {
    const accountId = this.authService.GetAccount();
    const workspaceId = this.authService.GetWorkspace();
    if (!accountId || !workspaceId) {
      return;
    }

    this.workspaceContextService.list().subscribe({
      next: (accounts) => {
        const account = accounts.find((item) => item.id === accountId);
        const workspace = account?.workspaces.find(
          (item) => item.id === workspaceId,
        );
        this.currentContext.set(
          account && workspace
            ? { accountName: account.name, workspaceName: workspace.name }
            : null,
        );
      },
      error: () => this.currentContext.set(null),
    });
  }
}
